/**
 * Performs a breadth-first search traversal of a directed graph.
 *
 * Yields tuples containing the current item and the previous item in the traversal.
 * Uses BFS to ensure nodes are visited level by level from the initial key.
 *
 * @template Graph - The graph type, a record mapping strings to items with a `next` property
 * @template Item - The item type, defaults to the values of the graph
 *
 * @param {Graph} graph - The graph object to traverse
 * @param {string} initialKey - The key to start the traversal from
 *
 * @yields {[Item, Item | undefined]} Tuples of [currentItem, previousItem] during traversal.
 *                                     The previousItem is undefined for the first yielded item.
 *
 * @example
 * const graph = {
 *   a: { next: ['b', 'c'] },
 *   b: { next: ['d'] },
 *   c: { next: ['d'] },
 *   d: { next: [] }
 * };
 *
 * for (const [current, previous] of getGraphBFS(graph, 'a')) {
 *   console.log(current, previous);
 * }
 * // Yields: [{ next: ['b', 'c'] }, undefined]
 * //         [{ next: ['d'] }, { next: ['b', 'c'] }]
 * //         [{ next: ['d'] }, { next: ['d'] }]
 * //         [{ next: [] }, { next: ['d'] }]
 */
export function* getGraphBFS(graph, initialKey) {
  const queue = [initialKey];
  const visited = {};
  visited[initialKey] = true;
  let currentVertex;
  let previousVertex;
  while (queue.length) {
    currentVertex = queue.shift();
    if (currentVertex === undefined) {
      continue;
    }
    const item = graph[currentVertex];
    if (item === undefined) {
      continue;
    }
    const previousItem = previousVertex ? graph[previousVertex] : undefined;
    yield [item, previousItem];
    previousVertex = currentVertex;
    for (const neighbor of item.next ?? []) {
      if (!visited[neighbor]) {
        visited[neighbor] = true;
        queue.push(neighbor);
      }
    }
  }
}

/**
 * Creates an iterable generator for traversing a graph starting from its initial key.
 *
 * This is a convenience wrapper around `getGraphBFS` that automatically determines
 * the initial key of the graph before starting the traversal.
 *
 * @template Graph - The graph type, a record mapping strings to items with a `next` property
 * @template Item - The item type, defaults to the values of the graph
 *
 * @param {Graph} flow - The graph object to create an iterator for
 *
 * @yields {[Item, Item | undefined]} Tuples of [currentItem, previousItem] during traversal
 * @returns {undefined} Returns undefined when the traversal is complete
 *
 * @throws {Error} Throws an error if the graph doesn't have a valid initial key
 *
 * @example
 * const flow = {
 *   initial: { next: ['step1'] },
 *   step1: { next: ['step2'] },
 *   step2: { next: [] }
 * };
 *
 * for (const [current, previous] of getIterableGraph(flow)) {
 *   console.log(`Current: ${current}, Previous: ${previous}`);
 * }
 */
export function* getIterableGraph(flow) {
  let initialKey = getGraphInitialKey(flow);
  for (const item of getGraphBFS(flow, initialKey)) {
    yield item;
  }
}

/**
 * Determines the initial key for traversing a graph.
 *
 * The function first checks if the graph has an 'initial' key. If not,
 * it attempts to find the initial key by analyzing the graph structure.
 *
 * @template Item - The item type with a required `next` property
 * @template Graph - The graph type mapping string keys to items
 *
 * @param {Graph} flow - The graph object to find the initial key for
 *
 * @returns {string} The initial key for the graph
 *
 * @throws {Error} Throws "Flow object must contain an initial key" if no valid initial key is found
 *
 * @example
 * // Graph with explicit 'initial' key
 * const flow1 = {
 *   initial: { next: ['step1'] },
 *   step1: { next: [] }
 * };
 * getGraphInitialKey(flow1); // Returns: 'initial'
 *
 * @example
 * // Graph without 'initial' key - finds the unreferenced node
 * const flow2 = {
 *   start: { next: ['end'] },
 *   end: { next: [] }
 * };
 * getGraphInitialKey(flow2); // Returns: 'start'
 */
export function getGraphInitialKey(flow) {
  let initialKey = null;
  if (flow.initial) {
    initialKey = 'initial';
  } else {
    initialKey = findGraphInitialItemKey(flow);
    if (!initialKey) {
      throw new Error('Flow object must contain an initial key');
    }
  }
  return initialKey;
}

/**
 * Finds the key of the initial item in a graph by identifying unreferenced nodes.
 *
 * An initial item is one that is not referenced in any other item's `next` array.
 * This function returns null if there are no initial items or multiple initial items.
 *
 * @template Item - The item type with a required `next` property containing an array of strings
 * @template Graph - The graph type, a record mapping string keys to items
 *
 * @param {Graph} graph - The graph object to analyze
 *
 * @returns {string | null} The key of the single initial item, or null if there are zero or multiple initial items
 *
 * @example
 * // Single initial item
 * const graph1 = {
 *   start: { next: ['middle'] },
 *   middle: { next: ['end'] },
 *   end: { next: [] }
 * };
 * findGraphInitialItemKey(graph1); // Returns: 'start'
 *
 * @example
 * // Multiple initial items
 * const graph2 = {
 *   branch1: { next: ['end'] },
 *   branch2: { next: ['end'] },
 *   end: { next: [] }
 * };
 * findGraphInitialItemKey(graph2); // Returns: null
 *
 * @example
 * // Circular graph with no initial items
 * const graph3 = {
 *   a: { next: ['b'] },
 *   b: { next: ['a'] }
 * };
 * findGraphInitialItemKey(graph3); // Returns: null
 */
export function findGraphInitialItemKey(graph) {
  const allItems = new Set(findGraphInitialItems(graph).keys());
  if (allItems.size === 1) {
    const initial = allItems.values().next().value;
    return initial ?? null;
  }

  // If no initial item is found or multiple initial items, return null
  return null;
}

/**
 * Finds all initial items in a directed graph structure.
 *
 * An initial item is defined as an item that is not referenced in any other item's `next` array.
 * This function is useful for identifying entry points in a flow or state machine graph.
 *
 * @template K - The key type for the graph items, must extend string
 * @template Item - The item type, must have an optional `next` property containing an array of strings
 * @template Graph - The graph type, a record mapping keys to items
 *
 * @param {Graph} graph - The graph object to analyze. Each item in the graph can have a `next` property
 *                        that contains an array of keys pointing to other items in the graph.
 *
 * @returns {Map<K, Graph[K]>} A Map containing all initial items (items that are not referenced by any other item).
 *                              The Map keys are the item names and values are the item objects themselves.
 *
 * @example
 * // Simple linear flow
 * const graph = {
 *   step1: { next: ['step2'] },
 *   step2: { next: ['step3'] },
 *   step3: { next: [] }
 * };
 * const initialItems = findGraphInitialItems(graph);
 * // Returns: Map { 'step1' => { next: ['step2'] } }
 *
 * @example
 * // Multiple initial items (parallel flows)
 * const graph = {
 *   flow1Start: { next: ['flow1Middle'] },
 *   flow1Middle: { next: ['shared'] },
 *   flow2Start: { next: ['flow2Middle'] },
 *   flow2Middle: { next: ['shared'] },
 *   shared: { next: [] }
 * };
 * const initialItems = findGraphInitialItems(graph);
 * // Returns: Map {
 * //   'flow1Start' => { next: ['flow1Middle'] },
 * //   'flow2Start' => { next: ['flow2Middle'] }
 * // }
 *
 * @example
 * // Graph with cycles
 * const graph = {
 *   nodeA: { next: ['nodeB'] },
 *   nodeB: { next: ['nodeC'] },
 *   nodeC: { next: ['nodeA'] }  // Creates a cycle
 * };
 * const initialItems = findGraphInitialItems(graph);
 * // Returns: Map {} (empty, as all nodes are referenced)
 */
export function findGraphInitialItems(graph) {
  const allItems = new Map();
  const referencedItems = new Set();
  for (const [key, value] of Object.entries(graph)) {
    allItems.set(key, value);
    value.next?.forEach(next => referencedItems.add(next));
  }
  for (const referenced of referencedItems) {
    allItems.delete(referenced);
  }
  return allItems;
}